<template>
  <v-container fluid>
    <v-row align="center" justify="space-between" class="mb-4">
      <v-col cols="12" md="8" class="d-flex align-center">
        <v-btn text color="primary" class="mr-3" @click="$router.back()">
          <v-icon left>mdi-arrow-left</v-icon>
          Volver
        </v-btn>
        <h2 class="text-h5 font-weight-bold">{{ orden ? orden.numero_display : 'Orden de producción' }}</h2>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right">
        <v-chip v-if="orden" :color="estadoColor(orden.estado)" dark>{{ estadoLabel(orden.estado) }}</v-chip>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col v-for="i in 3" :key="i" cols="12"><v-skeleton-loader type="list-item-three-line" /></v-col>
    </v-row>

    <template v-else-if="orden">
      <v-row>
        <!-- Panel izquierdo -->
        <v-col cols="12" md="4">
          <v-card class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold">Datos</v-card-title>
            <v-card-text>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Receta</span>
                <span class="caption">{{ orden.receta_nombre }}</span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Máquina</span>
                <span class="caption">{{ orden.maquina_nombre || '—' }}</span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Lotes</span>
                <span class="caption">{{ orden.cantidad_lotes }}</span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Fecha</span>
                <span class="caption">{{ orden.fecha_alta }}</span>
              </div>
              <div v-if="orden.fecha_inicio" class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Iniciada</span>
                <span class="caption">{{ orden.fecha_inicio | fechaHora }}</span>
              </div>
              <div v-if="orden.fecha_confirmacion" class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Finalizada</span>
                <span class="caption">{{ orden.fecha_confirmacion }}</span>
              </div>
              <div v-if="orden.duracion_real_minutos != null" class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Duración real</span>
                <span class="caption font-weight-bold">
                  {{ orden.duracion_real_minutos }} min
                  <span class="grey--text">(est. {{ orden.duracion_estimada_minutos }})</span>
                </span>
              </div>
            </v-card-text>
          </v-card>

          <!-- Costos (post confirmación) -->
          <v-card v-if="orden.estado === 'confirmada'" class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold">Costo de producción</v-card-title>
            <v-card-text>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Material</span>
                <span class="caption">{{ money(orden.costo_insumos_total) }}</span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Máquina</span>
                <span class="caption">{{ money(orden.costo_maquina_total) }}</span>
              </div>
              <v-divider class="my-2" />
              <div class="d-flex justify-space-between">
                <span class="font-weight-bold">Total</span>
                <span class="font-weight-bold">{{ money(totalCosto) }}</span>
              </div>
            </v-card-text>
          </v-card>

          <!-- Acciones: borrador -->
          <v-card v-if="orden.estado === 'borrador'">
            <v-card-title class="subtitle-1 font-weight-bold">Acciones</v-card-title>
            <v-card-text class="d-flex flex-column" style="gap: 8px">
              <v-btn color="deep-purple" dark :loading="loadingIniciar" @click="iniciar">
                <v-icon left>mdi-play</v-icon>
                Iniciar (cronómetro)
              </v-btn>
              <v-btn color="success" outlined :loading="loadingConfirmar" @click="confirmar">
                <v-icon left>mdi-play-circle</v-icon>
                Confirmar directo
              </v-btn>
              <v-btn color="error" outlined @click="dialogCancelar = true">
                <v-icon left>mdi-cancel</v-icon> Cancelar
              </v-btn>
              <v-btn text color="grey darken-1" @click="dialogEliminar = true">
                <v-icon left>mdi-delete</v-icon> Eliminar
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Acciones: en proceso -->
          <v-card v-else-if="orden.estado === 'en_proceso'" style="border: 1px solid #FFA000">
            <v-card-title class="subtitle-1 font-weight-bold amber--text text--darken-2">
              <v-icon left color="amber darken-2">mdi-progress-clock</v-icon> En proceso
            </v-card-title>
            <v-card-text class="d-flex flex-column" style="gap: 8px">
              <div class="text-center mb-2">
                <div class="text-h4 font-weight-bold amber--text text--darken-2" style="font-variant-numeric: tabular-nums">
                  {{ cronometro }}
                </div>
                <div class="caption grey--text">estimado {{ orden.duracion_estimada_minutos }} min</div>
              </div>
              <v-btn color="success" :loading="loadingConfirmar" @click="confirmar">
                <v-icon left>mdi-flag-checkered</v-icon> Finalizar y producir
              </v-btn>
              <v-btn color="error" outlined @click="dialogCancelar = true">
                <v-icon left>mdi-cancel</v-icon> Cancelar
              </v-btn>
            </v-card-text>
          </v-card>

          <v-alert v-else-if="orden.estado === 'confirmada'" type="success" text dense>
            Producción ejecutada: stock actualizado.
          </v-alert>
        </v-col>

        <!-- Panel derecho -->
        <v-col cols="12" md="8">
          <!-- Confirmada: snapshots -->
          <template v-if="orden.estado === 'confirmada'">
            <v-card class="mb-4">
              <v-card-title class="subtitle-1 font-weight-bold red--text text--darken-1">
                <v-icon left color="red darken-1">mdi-tray-arrow-down</v-icon> Consumió
              </v-card-title>
              <v-simple-table>
                <thead>
                  <tr>
                    <th>Insumo</th>
                    <th class="text-right">Cantidad</th>
                    <th class="text-right">Costo unit.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in orden.consumos" :key="'c' + c.id">
                    <td>{{ c.descripcion }}</td>
                    <td class="text-right">{{ num(c.cantidad) }}</td>
                    <td class="text-right">{{ money(c.costo_unitario) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>

            <v-card>
              <v-card-title class="subtitle-1 font-weight-bold green--text text--darken-1">
                <v-icon left color="green darken-1">mdi-tray-arrow-up</v-icon> Produjo
              </v-card-title>
              <v-simple-table>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th class="text-right">Cantidad</th>
                    <th class="text-right">Costo unit.</th>
                    <th class="text-right">Precio sugerido</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in orden.salidas" :key="'s' + s.id">
                    <td>{{ s.descripcion }}</td>
                    <td class="text-right">{{ num(s.cantidad) }}</td>
                    <td class="text-right">{{ money(s.costo_unitario_calculado) }}</td>
                    <td class="text-right">
                      <span v-if="parseFloat(s.precio_sugerido) > 0" class="green--text font-weight-medium">
                        {{ money(s.precio_sugerido) }}
                      </span>
                      <span v-else class="grey--text">—</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>
          </template>

          <!-- Borrador: preview de lo que va a hacer -->
          <v-card v-else>
            <v-card-title class="subtitle-1 font-weight-bold">
              Vista previa
              <v-spacer />
              <span class="caption grey--text">x {{ orden.cantidad_lotes }} lote(s)</span>
            </v-card-title>
            <v-card-text v-if="receta">
              <p class="overline mb-1 red--text text--darken-1">Consumirá</p>
              <v-simple-table dense>
                <tbody>
                  <tr v-for="c in receta.consumos" :key="'rc' + c.id">
                    <td>{{ c.descripcion }}</td>
                    <td class="text-right">{{ porLote(c.cantidad) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <p class="overline mb-1 mt-4 green--text text--darken-1">Producirá</p>
              <v-simple-table dense>
                <tbody>
                  <tr v-for="s in receta.salidas" :key="'rs' + s.id">
                    <td>{{ s.descripcion }}</td>
                    <td class="text-right">{{ porLote(s.cantidad) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
            <v-card-text v-else class="grey--text">Cargando receta…</v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <dialog-confirmation-close
      v-if="dialogCancelar"
      @close-dialog="dialogCancelar = false"
      @process-accept="cancelar"
    >
      <template v-slot:titulo>¿Cancelar orden?</template>
      <template v-slot:message>No se moverá stock. Esta acción no se puede revertir.</template>
      <template v-slot:buttonOK>Cancelar orden</template>
    </dialog-confirmation-close>

    <dialog-confirmation-close
      v-if="dialogEliminar"
      @close-dialog="dialogEliminar = false"
      @process-accept="eliminar"
    >
      <template v-slot:titulo>¿Eliminar orden?</template>
      <template v-slot:message>Se borra el borrador definitivamente.</template>
      <template v-slot:buttonOK>Eliminar</template>
    </dialog-confirmation-close>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';
import DialogConfirmationClose from '@/components/dialogs/confirmation.close.vue';

export default {
  name: 'OrdenProduccionConsultar',
  components: { DialogConfirmationClose },
  data() {
    return {
      orden: null,
      receta: null,
      loading: false,
      loadingConfirmar: false,
      loadingIniciar: false,
      dialogCancelar: false,
      dialogEliminar: false,
      ahora: Date.now(),
      timer: null,
    };
  },
  filters: {
    fechaHora(v) {
      if (!v) return '';
      return new Date(v).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    },
  },
  computed: {
    ordenId() {
      return this.$route.params.id;
    },
    totalCosto() {
      if (!this.orden) return 0;
      return (parseFloat(this.orden.costo_insumos_total) || 0) + (parseFloat(this.orden.costo_maquina_total) || 0);
    },
    cronometro() {
      if (!this.orden || !this.orden.fecha_inicio) return '00:00';
      const s = Math.max(0, Math.floor((this.ahora - new Date(this.orden.fecha_inicio).getTime()) / 1000));
      const pad = n => String(n).padStart(2, '0');
      const hh = Math.floor(s / 3600);
      const mm = Math.floor((s % 3600) / 60);
      const ss = s % 60;
      return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
    },
  },
  methods: {
    ...mapActions('ordenProduccion', ['get_orden', 'iniciar_orden', 'confirmar_orden', 'cancelar_orden', 'delete_orden']),
    ...mapActions('recetaProduccion', ['get_receta']),

    estadoColor(e) {
      return { borrador: 'grey', en_proceso: 'amber darken-2', confirmada: 'green', cancelada: 'red darken-1' }[e] || 'grey';
    },
    estadoLabel(e) {
      return { borrador: 'Pendiente', en_proceso: 'En proceso', confirmada: 'Confirmada', cancelada: 'Cancelada' }[e] || e;
    },
    money(v) {
      if (v == null) return '—';
      return '$' + Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    num(v) {
      return Number(parseFloat(v)).toLocaleString('es-AR');
    },
    porLote(cant) {
      const n = (parseFloat(cant) || 0) * (this.orden ? parseFloat(this.orden.cantidad_lotes) : 1);
      return Number(n).toLocaleString('es-AR');
    },
    snack(color, text, icon) {
      this.$updateBus.$emit('showSnackBar', { show: true, button: false, color, text, icon, timeout: 3500 });
    },

    async fetchOrden() {
      this.loading = true;
      try {
        const res = await this.get_orden(this.ordenId);
        this.orden = res.data;
        if (['borrador', 'en_proceso'].includes(this.orden.estado) && this.orden.receta) {
          const r = await this.get_receta(this.orden.receta);
          this.receta = r.data;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    async iniciar() {
      this.loadingIniciar = true;
      try {
        await this.iniciar_orden(this.ordenId);
        this.snack('info', 'Orden en proceso ⏱️', 'mdi-play');
        await this.fetchOrden();
      } catch (e) {
        const msg = e.response?.data?.error || 'No se pudo iniciar';
        this.snack('error', msg, 'mdi-alert');
      } finally {
        this.loadingIniciar = false;
      }
    },

    async confirmar() {
      this.loadingConfirmar = true;
      try {
        await this.confirmar_orden(this.ordenId);
        this.snack('success', 'Producción finalizada: stock actualizado', 'mdi-check');
        await this.fetchOrden();
      } catch (e) {
        const msg = e.response?.data?.error || 'No se pudo finalizar la orden';
        this.snack('error', msg, 'mdi-alert');
      } finally {
        this.loadingConfirmar = false;
      }
    },

    async cancelar() {
      this.dialogCancelar = false;
      try {
        await this.cancelar_orden(this.ordenId);
        this.snack('success', 'Orden cancelada', 'mdi-check');
        await this.fetchOrden();
      } catch (e) {
        const msg = e.response?.data?.error || 'No se pudo cancelar';
        this.snack('error', msg, 'mdi-alert');
      }
    },

    async eliminar() {
      this.dialogEliminar = false;
      try {
        await this.delete_orden(this.ordenId);
        this.snack('success', 'Orden eliminada', 'mdi-check');
        this.$router.push({ name: 'empresa.produccion.orden.listado' });
      } catch (e) {
        const msg = e.response?.data?.error || 'No se pudo eliminar';
        this.snack('error', msg, 'mdi-alert');
      }
    },
  },
  created() {
    this.fetchOrden();
    this.timer = setInterval(() => { this.ahora = Date.now(); }, 1000);
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },
};
</script>
